import { useState } from "react";
import { useLoaderData, type LoaderFunctionArgs } from "react-router-dom";
import { publicAPI, listenerSubscriptionAPI } from "../../lib/api";
import styles from "./SubscribePage.module.css";

interface PricingCard {
  id: string;
  interval: "monthly" | "yearly";
  priceCents: number;
  trialDays: number;
  active: boolean;
}

interface LoaderData {
  podcast: {
    id: string;
    name: string;
    slug: string;
    description: string;
    coverArtworkUrl: string | null;
    author: string | null;
  };
  pricing: PricingCard[];
}

export async function subscribeLoader({ params }: LoaderFunctionArgs) {
  const { slug } = params;
  if (!slug) throw new Response("Not Found", { status: 404 });
  const data = await publicAPI.getPodcast(slug);
  return data;
}

function formatPrice(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

function calcSavingsPercent(monthly: PricingCard, yearly: PricingCard): number {
  const yearlyEquivalent = monthly.priceCents * 12;
  return Math.round(((yearlyEquivalent - yearly.priceCents) / yearlyEquivalent) * 100);
}

export default function SubscribePage() {
  const { podcast, pricing } = useLoaderData() as LoaderData;
  const activePricing = pricing.filter((p) => p.active);
  const monthly = activePricing.find((p) => p.interval === "monthly");
  const yearly = activePricing.find((p) => p.interval === "yearly");

  const [selectedInterval, setSelectedInterval] = useState<"monthly" | "yearly">(
    monthly ? "monthly" : "yearly",
  );
  const [email, setEmail] = useState("");
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const savingsPercent = monthly && yearly ? calcSavingsPercent(monthly, yearly) : 0;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email || !agreedToTerms) return;

    setLoading(true);
    setError(null);

    try {
      const result = await listenerSubscriptionAPI.checkout({
        podcastId: podcast.id,
        email,
        interval: selectedInterval,
        successUrl: window.location.origin + "/subscribe/" + podcast.slug + "/success",
        cancelUrl: window.location.href,
      });
      window.location.href = result.url;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong");
      setLoading(false);
    }
  }

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        <div className={styles.podcastHeader}>
          {podcast.coverArtworkUrl && (
            <img
              src={podcast.coverArtworkUrl}
              alt={`${podcast.name} cover art`}
              className={styles.artwork}
            />
          )}
          <div className={styles.podcastInfo}>
            <h1 className={styles.podcastName}>{podcast.name}</h1>
            {podcast.author && <p className={styles.author}>by {podcast.author}</p>}
            {podcast.description && <p className={styles.description}>{podcast.description}</p>}
          </div>
        </div>

        {activePricing.length === 0 ? (
          <p className={styles.noPricing}>
            Subscriptions are not currently available for this podcast.
          </p>
        ) : (
          <form onSubmit={handleSubmit} className={styles.form}>
            <h2 className={styles.sectionTitle}>Choose a plan</h2>

            <div className={styles.pricingCards}>
              {monthly && (
                <button
                  type="button"
                  className={`${styles.pricingCard} ${selectedInterval === "monthly" ? styles.selected : ""}`}
                  onClick={() => setSelectedInterval("monthly")}
                  aria-pressed={selectedInterval === "monthly"}
                >
                  <span className={styles.interval}>Monthly</span>
                  <span className={styles.price}>{formatPrice(monthly.priceCents)}/mo</span>
                </button>
              )}
              {yearly && (
                <button
                  type="button"
                  className={`${styles.pricingCard} ${selectedInterval === "yearly" ? styles.selected : ""}`}
                  onClick={() => setSelectedInterval("yearly")}
                  aria-pressed={selectedInterval === "yearly"}
                >
                  <span className={styles.interval}>Yearly</span>
                  <span className={styles.price}>{formatPrice(yearly.priceCents)}/yr</span>
                  {savingsPercent > 0 && (
                    <span className={styles.savingsBadge}>Save {savingsPercent}%</span>
                  )}
                </button>
              )}
            </div>

            <div className={styles.fieldGroup}>
              <label htmlFor="email" className={styles.label}>
                Email address
              </label>
              <input
                id="email"
                type="email"
                required
                className={styles.input}
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            <label className={styles.checkbox}>
              <input
                type="checkbox"
                checked={agreedToTerms}
                onChange={(e) => setAgreedToTerms(e.target.checked)}
                required
              />
              <span>I agree to the terms of service</span>
            </label>

            {error && <p className={styles.error}>{error}</p>}

            <button
              type="submit"
              className={styles.submitButton}
              disabled={loading || !email || !agreedToTerms}
            >
              {loading ? "Redirecting…" : "Subscribe"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
